import React, { useEffect, useMemo, useState } from 'react';
import { partnerApiService } from '../../services/api';
import {
  AnalyticsMetric,
  AnalyticsPeriod,
  AnalyticsResponse,
  AnalyticsSeriesPoint,
} from '../../types/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { TranslationSchema } from '../../locales';

/**
 * Дельта к прошлому периоду.
 *
 * null с бэка означает «в прошлом периоде событий не было» — сбор трафика
 * начался недавно, и сравнивать не с чем. Показываем «нет данных», а не
 * «0%»: ноль был бы ложью о существующем прошлом.
 */
const DeltaBadge: React.FC<{ delta: number | null; t: TranslationSchema }> = ({ delta, t }) => {
  if (delta === null) {
    return <span className="qp-kpi-delta qp-kpi-delta--none">{t.cabinet.deltaNoData}</span>;
  }
  const direction = delta > 0 ? 'up' : delta < 0 ? 'down' : 'flat';
  const sign = delta > 0 ? '+' : '';
  return (
    <span className={`qp-kpi-delta qp-kpi-delta--${direction}`}>
      {sign}
      {delta}% {t.cabinet.deltaToPrev}
    </span>
  );
};

/**
 * Спарклайн метрики: полилиния по дням окна, без осей и подписей.
 *
 * Ряд приходит с бэка полным (дни без событий — нулями), поэтому здесь
 * только геометрия. Плоская нулевая серия рисует линию по низу — «ничего
 * не происходило» тоже форма, прятать её не нужно.
 */
const Sparkline: React.FC<{ series: AnalyticsSeriesPoint[]; ariaLabel: string }> = ({ series, ariaLabel }) => {
  if (series.length < 2) return null;

  const width = 120;
  const height = 28;
  const max = Math.max(...series.map((point) => point.value), 1);

  const points = series
    .map((point, index) => {
      const x = (index / (series.length - 1)) * width;
      const y = height - 2 - (point.value / max) * (height - 4);
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');

  return (
    <svg
      className="qp-kpi-spark"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={ariaLabel}
    >
      <polyline points={points} fill="none" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
};

/**
 * Трафик заведения: счётчики открытий из partner_events с дельтой.
 *
 * Живёт отдельно от KpiRow: там статические цифры аккаунта (категории,
 * рейтинг), здесь — поведение клиентов за выбранное окно.
 */
export const TrafficPanel: React.FC = () => {
  const { t } = useLanguage();
  const [period, setPeriod] = useState<AnalyticsPeriod>('30d');
  const [data, setData] = useState<AnalyticsResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const periods = useMemo<{ value: AnalyticsPeriod; label: string }[]>(() => [
    { value: '7d', label: t.cabinet.traffic7d },
    { value: '30d', label: t.cabinet.traffic30d },
    { value: '90d', label: t.cabinet.traffic90d },
  ], [t]);

  const metrics = useMemo<{ key: keyof AnalyticsResponse['metrics']; label: string; note: string }[]>(() => [
    { key: 'partner_views', label: t.cabinet.metricPartnerViews, note: t.cabinet.metricPartnerViewsNote },
    { key: 'catalog_opens', label: t.cabinet.metricCatalogOpens, note: t.cabinet.metricCatalogOpensNote },
    { key: 'discount_opens', label: t.cabinet.metricDiscountOpens, note: t.cabinet.metricDiscountOpensNote },
    { key: 'contact_clicks', label: t.cabinet.metricContactClicks, note: t.cabinet.metricContactClicksNote },
    // Оффлайн-канал (QR и NFC) — отдельными плитками после онлайновых: страница
    // QR-кодов обещает партнёру, что сканы видно в аналитике на главной.
    { key: 'qr_scans', label: t.cabinet.metricQrScans, note: t.cabinet.metricQrScansNote },
    { key: 'nfc_taps', label: t.cabinet.metricNfcTaps, note: t.cabinet.metricNfcTapsNote },
  ], [t]);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    partnerApiService
      .getAnalytics(period)
      .then((response) => {
        if (!cancelled) setData(response);
      })
      .catch((error) => console.error('Ошибка загрузки аналитики:', error))
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [period]);

  return (
    <section className="qp-panel" aria-labelledby="traffic-heading">
      <header className="qp-panel-head">
        <h2 id="traffic-heading" className="qp-label">
          {t.cabinet.trafficTitle}
        </h2>
        <div className="qp-chips" role="group" aria-label={t.cabinet.trafficPeriod}>
          {periods.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              className="qp-chip"
              aria-pressed={period === value}
              onClick={() => setPeriod(value)}
            >
              {label}
            </button>
          ))}
        </div>
      </header>

      {isLoading && !data ? (
        <div className="qp-kpi-grid">
          {metrics.map(({ key }) => (
            <div key={key} className="qp-skel" style={{ height: 104 }} />
          ))}
        </div>
      ) : data ? (
        <div className="qp-kpi-grid">
          {metrics.map(({ key, label, note }) => {
            const metric: AnalyticsMetric = data.metrics[key];
            return (
              <article key={key} className="qp-kpi">
                <span className="qp-label">{label}</span>
                <p className="qp-kpi-value">{metric.value}</p>
                <Sparkline series={metric.series ?? []} ariaLabel={t.cabinet.sparklineAria} />
                <DeltaBadge delta={metric.delta_percent} t={t} />
                <span className="qp-kpi-note">{note}</span>
              </article>
            );
          })}
        </div>
      ) : null}
    </section>
  );
};
